import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import Button from '@/components/homepage/Button';
import Input, { InputType } from '@/components/Form/Input';
import Title from '@/components/Title/Title';
import { postContactUs, postKlaviyo, klaviyoIdentify } from '@/services/forms.service';
import { getCountry } from '@/utils/i18n';
import beTwMerge from '@/utils/beTwMerge';
import { getAnchorId } from '@/utils/builderUtils';
import useStore from '@/store/useStore';
import RichText from '@/components/RichText/RichText';
import { FadeIn } from '@/components/Footer/components/FadeIn';
import { sha256 } from '@/utils/utils';
import useMD5HashEmail from '@/hooks/useMD5HashEmail';

interface IContactUsForm {
  submitLabel?: string;
  title: string;
  description: string;
  successText: string;
  cta: {
    variant: 'primary' | 'secondary';
    color: 'light' | 'dark';
    label: string;
  };
  image: {
    url: string;
    alt: string;
  };
  anchorLinkID?: string;
}

export default function ContactUsForm({ title, description, cta, image, anchorLinkID, successText }: IContactUsForm) {
  const user = useStore(state => state.user);
  const isEurope = getCountry().code === 'GB' || user?.isEuropeanUnionMember;
  const hasDescription = !!description?.replace(/(<([^>]+)>)/gi, '')?.trim();
  const inputs: InputType[] = [
    {
      name: 'first_name',
      id: 'firstName',
      label: 'First Name',
      type: 'text',
      registerObject: {
        required: true,
      },
    },
    {
      name: 'last_name',
      id: 'lastName',
      label: 'Last Name',
      type: 'text',
      registerObject: {
        required: true,
      },
    },
    {
      name: 'email',
      id: 'email',
      label: 'Email',
      fullWidth: true,
      type: 'email',
      registerObject: {
        required: true,
        validate: {
          isValid: v => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/.test(v) || 'Invalid Email Address.',
        },
      },
    },
    {
      name: 'phone',
      id: 'phone',
      label: 'Phone Number',
      fullWidth: true,
      type: 'tel',
      registerObject: {
        validate: {
          isValid: v =>
            !v ||
            /^\(\d{3}\)[\s](\d{3})[-](\d{4})$/.test(v) ||
            /^\d{3}[-](\d{3})[-](\d{4})$/.test(v) ||
            /^\d{10}$/.test(v) ||
            'Invalid phone number.',
        },
      },
    },
    {
      name: 'message',
      id: 'message',
      label: 'Message',
      fullWidth: true,
      type: 'textarea',
    },
    {
      name: 'subscription',
      id: 'subscription',
      label: isEurope
        ? '<p>Send me email updates on new styles and special offers from Brilliant Earth. I may withdraw my consent at any time through my account settings or by writing to privacy@brilliantearth.com.</p>'
        : '<p>Send me updates on new styles and special offers.</p>',
      type: 'checkbox',
      className: 'tw-mt-[-36px]',
      fullWidth: true,
    },
  ];
  const {
    register,
    getValues,
    setFocus,
    trigger,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: { subscription: false } });
  const getFirstError = useCallback(
    () => Object.keys(errors).reduce<any>((field, a) => (errors[field] ? field : a), null),
    [errors]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitLabel, setSubmitLabel] = useState<string | boolean>('');
  const { hashEmail } = useMD5HashEmail();
  function centerSuccessText() {
    setTimeout(() => {
      const element = document.getElementById('success-text')!;
      element.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'nearest' });
    }, 100);
  }
  if (isEurope) {
    inputs.push({
      name: 'privacyPolicyTermsConditions',
      id: 'privacyPolicyTermsConditions',
      fullWidth: true,
      type: 'checkbox',
      className: 'tw-mt-[-28px]',
      label:
        "I agree to Brilliant Earth's <a href='https://www.brilliantearth.com/en-gb/security-and-privacy-policies/#privacy' target='_blank'>Privacy Policy</a> and <a href='https://www.brilliantearth.com/en-gb/terms-and-conditions/' target='_blank'>Terms and Conditions</a>.",
      registerObject: {
        required: 'Agreement to our Privacy Policy and Terms and Conditions is required.',
      },
    });
  }
  async function submitForm() {
    const isFormValid = await trigger();
    const { subscription, ...formData } = getValues() as any;
    if (isFormValid) {
      setIsSubmitting(true);
      setSubmitLabel('Sending');
      if (subscription) {
        postKlaviyo('Contact Us Page', formData.email);
      }

      postContactUs({ subscription: subscription ? 'T' : 'F', ...formData })
        .then(async result => {
          if (!result.status.error) {
            reset();
            setSubmitLabel(true);
            const hashedEmail = await hashEmail(formData.email);
            klaviyoIdentify({
              email: formData.email,
              firstName: formData.first_name,
              lastName: formData.last_name,
            });
            (window as any).dataLayer.push({
              userEmail: formData.email,
              userHashedEmail: await sha256(formData.email),
              userPhoneNumber: formData.phone,
              userFirstName: formData.first_name,
              userLastName: formData.last_name,
              hashed_user_email_md5: hashedEmail,
            });
            (window as any).dataLayer.push({ event: 'email_request' });
            centerSuccessText();
          } else {
            throw new Error('Failed to send');
          }
        })
        .catch(() => {
          setSubmitLabel(cta?.label);
          // eslint-disable-next-line no-alert
          alert('Sent failed. Please try again.');
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      const firstError = getFirstError();
      if (getFirstError()) setFocus<any>(firstError);
    }
  }

  useEffect(() => {
    const firstError = getFirstError();
    if (firstError) {
      setFocus<any>(firstError);
    }
  }, [getFirstError, setFocus]);

  return (
    <section
      className={beTwMerge(
        'tw-mx-4 lg:tw-mx-8 tw-mt-6 tw-mb-20 min-[1472px]:tw-mx-auto min-[1472px]:tw-w-full tw-max-w-[1440px]',
        submitLabel === true && 'tw-mb-14'
      )}
      {...getAnchorId(anchorLinkID)}
    >
      <div className="sm:tw-flex tw-gap-x-12 sm:tw-justify-between">
        <div className="min-[1172px]:tw-max-w-[682px] sm:tw-w-full lg:tw-pr-6">
          {submitLabel !== true && (
            <>
              <Title
                component="h2"
                weight="medium"
                size="large"
                alignment="left"
                className={beTwMerge(hasDescription ? '!tw-mb-2' : '!tw-mb-[2.75rem] md:!tw-mb-[3.75rem]')}
              >
                {title}
              </Title>
              {hasDescription && (
                <RichText className="tw-font-normal tw-text-left tw-mb-[3.25rem]" content={description} />
              )}
              <form>
                <div className="tw-grid md:tw-grid-cols-2 tw-mb-8 tw-gap-x-8 tw-gap-y-11">
                  {inputs.map(input => (
                    /* @ts-ignore */
                    <Input
                      {...input}
                      key={input.name}
                      register={register as any}
                      name={input.name}
                      id={input.id}
                      className={input.className}
                      error={errors[input.name] as any}
                    />
                  ))}
                </div>

                <div className="tw-col-span-full tw-flex tw-justify-start">
                  <Button
                    className="tw-min-w-[200px] tw-mt-[-9px]"
                    label={submitLabel || cta?.label}
                    color={cta?.color === 'light' ? 'white' : 'dark-green'}
                    type={cta?.variant}
                    onClick={submitForm}
                    disabled={isSubmitting}
                  />
                </div>
              </form>
            </>
          )}
          <FadeIn props={{ opacity: submitLabel === true ? 1 : 0, height: '100%' }} visible={submitLabel === true}>
            <p
              id="success-text"
              className="tw-font-[350] tw-flex tw-justify-center tw-items-center tw-h-full tw-text-center"
            >
              {successText}
            </p>
          </FadeIn>
        </div>

        <Image
          src={image?.url}
          alt={image?.alt || ''}
          className="tw-hidden md:tw-hidden min-[1172px]:tw-block"
          width="676"
          height="512"
        />
      </div>
    </section>
  );
}
